import React, { useState, useEffect } from 'react';
import {
  IconButton,
  Badge,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Typography,
  Box,
  Button,
  Divider,
  CircularProgress
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Mail as MailIcon,
  MailOutline as MailOutlineIcon,
  Message as MessageIcon
} from '@mui/icons-material';
import { useQuery, useQueryClient, useMutation } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { fetcher, mutation } from '../../utils/api';
import { ApiRoutes } from '../../constants/routes';
import { useAuthProvider } from '../../utils/auth';

const MessagesDropdown = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [isUploading, setIsUploading] = useState(() => localStorage.getItem('isUploading') === 'true');
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user, messagesRoute } = useAuthProvider();
  const theme = useTheme();
  const open = Boolean(anchorEl);

  // Watch for localStorage changes to detect upload state
  useEffect(() => {
    const checkUploadState = () => {
      setIsUploading(localStorage.getItem('isUploading') === 'true');
    };
    
    // Check on mount and set up storage event listener
    checkUploadState();
    window.addEventListener('storage', checkUploadState);
    
    // Also poll periodically since storage events don't fire in same tab
    const interval = setInterval(checkUploadState, 1000);
    
    return () => {
      window.removeEventListener('storage', checkUploadState);
      clearInterval(interval);
    };
  }, []);

  // Fetch unread count
  const { data: unreadData, isLoading: unreadLoading } = useQuery(
    ['unread-message-count'],
    () => fetcher(ApiRoutes.UnreadCount),
    {
      refetchInterval: isUploading ? false : 10000, // Disable polling during upload
      refetchOnWindowFocus: !isUploading, // Disable refetch on focus during upload
      enabled: !!localStorage.getItem('token') && !isUploading,
      staleTime: 5000, // Consider stale after 5 seconds
    }
  );

  // Fetch conversations
  const { data: conversations, isLoading: conversationsLoading } = useQuery(
    ['conversations'],
    () => fetcher(ApiRoutes.Conversations),
    {
      refetchInterval: isUploading ? false : 10000, // Disable polling during upload
      refetchOnWindowFocus: !isUploading, // Disable refetch on focus during upload
      enabled: !!localStorage.getItem('token') && !isUploading,
      staleTime: 5000, // Consider stale after 5 seconds
    }
  );

  const unreadCount = unreadData?.unread_count || 0;
  const conversationsList = conversations || [];
  // Sort conversations by last_message_at, most recent first
  const sortedConversations = [...conversationsList].sort((a, b) => {
    const dateA = a.last_message_at ? new Date(a.last_message_at) : new Date(0);
    const dateB = b.last_message_at ? new Date(b.last_message_at) : new Date(0);
    return dateB - dateA;
  });
  const recentConversations = sortedConversations.slice(0, 5); // Show 5 most recent

  // Mark messages as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: (conversationId) => mutation(
      `${ApiRoutes.MarkMessagesRead}${conversationId}/mark-read/`,
      {},
      'POST'
    ),
    onSuccess: () => {
      // Invalidate queries to update badges and conversation lists
      queryClient.invalidateQueries(['unread-message-count']);
      queryClient.invalidateQueries(['conversations']);
      queryClient.invalidateQueries(['unread-count']);
    }
  });

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const getMessagesRoute = (conversationId = null) => {
    // The messages page for who they're acting as
    return conversationId ? `${messagesRoute}/${conversationId}` : messagesRoute;
  };

  const handleViewAllMessages = () => {
    navigate(getMessagesRoute());
    handleClose();
  };

  const handleConversationClick = (conversationId, hasUnread) => {
    // Mark as read if there are unread messages
    if (hasUnread) {
      markAsReadMutation.mutate(conversationId);
    }
    navigate(getMessagesRoute(conversationId));
    handleClose();
  };

  const getConversationTitle = (conversation) => {
    if (conversation.title) {
      return conversation.title;
    }
    // If no title, try to get participant names
    if (conversation.participants && conversation.participants.length > 0) {
      const otherParticipants = conversation.participants.filter(
        p => p.user && p.user.email !== user?.email
      );
      if (otherParticipants.length > 0) {
        // Try to get names first, fall back to email
        const names = otherParticipants.map(p => {
          const user = p.user;
          if (user.first_name || user.last_name) {
            return `${user.first_name || ''} ${user.last_name || ''}`.trim();
          }
          return user.email || 'Unknown';
        });
        return names.join(', ');
      }
    }
    return 'Conversation';
  };

  const formatMessageTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getLastMessagePreview = (conversation) => {
    if (conversation.last_message) {
      const content = conversation.last_message.content || '';
      // Truncate long messages for better display
      return content.length > 50 ? content.substring(0, 50) + '...' : content;
    }
    return 'No messages yet';
  };

  return (
    <>
      <IconButton
        onClick={handleClick}
        sx={{ color: theme.palette.mode === 'light' ? theme.palette.primary.main : 'white' }}
        aria-label="messages"
      >
        <Badge badgeContent={unreadCount > 0 ? unreadCount : 0} color="error" invisible={unreadCount === 0}>
          {unreadCount > 0 ? (
            <MailIcon />
          ) : (
            <MailOutlineIcon />
          )}
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: 400,
            maxHeight: 500,
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            border: `1px solid ${theme.palette.primary.main}33`
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontFamily: 'Inter', fontWeight: 'bold' }}>
              Messages
            </Typography>
            {unreadCount > 0 && (
              <Typography variant="body2" sx={{ color: theme.palette.primary.main }}>
                {unreadCount} unread
              </Typography>
            )}
          </Box>

          {conversationsLoading || unreadLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={24} sx={{ color: theme.palette.primary.main }} />
            </Box>
          ) : recentConversations.length === 0 ? (
            <Box sx={{ textAlign: 'center', p: 3 }}>
              <MessageIcon sx={{ fontSize: 48, color: theme.palette.text.disabled, mb: 2 }} />
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                No messages yet
              </Typography>
            </Box>
          ) : (
            <>
              <List sx={{ maxHeight: 350, overflow: 'auto', p: 0 }}>
                {recentConversations.map((conversation) => {
                  const hasUnread = (conversation.unread_count || 0) > 0;
                  return (
                    <ListItem
                      key={conversation.id}
                      button
                      onClick={() => handleConversationClick(conversation.id, hasUnread)}
                      sx={{
                        '&:hover': {
                          backgroundColor: theme.palette.mode === 'dark' 
                            ? 'rgba(255, 255, 255, 0.05)' 
                            : 'rgba(0, 0, 0, 0.02)',
                        },
                        cursor: 'pointer',
                        backgroundColor: hasUnread 
                          ? `${theme.palette.primary.main}1A` 
                          : 'transparent',
                      }}
                    >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: theme.palette.primary.main, color: theme.palette.primary.contrastText }}>
                        {getConversationTitle(conversation).charAt(0).toUpperCase()}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: conversation.unread_count > 0 ? 'bold' : 'normal' }}>
                            {getConversationTitle(conversation)}
                          </Typography>
                          {conversation.unread_count > 0 && (
                            <Box
                              sx={{
                                backgroundColor: theme.palette.primary.main,
                                color: theme.palette.primary.contrastText,
                                borderRadius: '10px',
                                minWidth: 20,
                                height: 20,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.7rem',
                                fontWeight: 'bold',
                                px: 0.5,
                              }}
                            >
                              {conversation.unread_count}
                            </Box>
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: theme.palette.text.secondary,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              display: 'block',
                            }}
                          >
                            {getLastMessagePreview(conversation)}
                          </Typography>
                          {conversation.last_message_at && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: theme.palette.text.secondary,
                                fontSize: '0.65rem',
                                mt: 0.25,
                              }}
                            >
                              {formatMessageTime(conversation.last_message_at)}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                  );
                })}
              </List>
              <Divider sx={{ my: 1, borderColor: theme.palette.divider }} />
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
                <Button
                  onClick={handleViewAllMessages}
                  sx={{
                    color: theme.palette.primary.main,
                    textTransform: 'none',
                    fontWeight: 'bold',
                    '&:hover': {
                      backgroundColor: `${theme.palette.primary.main}1A`,
                    },
                  }}
                >
                  View All Messages
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default MessagesDropdown;

